import React, { createContext, useState, useContext, useEffect } from 'react';
import { 
  signup as apiSignup, 
  login as apiLogin, 
  getCurrentUser,
  verifyEmailOtp as apiVerifyEmailOtp,
  resendEmailOtp as apiResendEmailOtp,
  forgotPassword as apiForgotPassword,
  verifyPasswordResetOtp as apiVerifyPasswordResetOtp,
  resetPassword as apiResetPassword,
  companySignup as apiCompanySignup,
  companyLogin as apiCompanyLogin,
  getCurrentCompany
} from '../utils/api';

const AuthContext = createContext();

export { AuthContext };

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [token, setToken] = useState(null);
  const [mode, setMode] = useState(null); // 'open', 'school', 'college', or 'company'
  const [role, setRole] = useState(null); // 'student' or 'company'
  const [collegeId, setCollegeId] = useState(null); // Current college context
  const [userType, setUserType] = useState(null); // 'student' or 'company' (legacy, use role instead)
  const [initializing, setInitializing] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check for existing authentication on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const storedToken = localStorage.getItem('token');
      const storedMode = localStorage.getItem('mode');
      const storedRole = localStorage.getItem('role');
      const storedCollegeId = localStorage.getItem('collegeId');
      const storedUserType = localStorage.getItem('userType'); // Legacy support
      
      if (storedToken) {
        setToken(storedToken);
        setMode(storedMode);
        
        // Use role if available, fallback to userType for backward compatibility
        const effectiveRole = storedRole || storedUserType;
        setRole(effectiveRole);
        setUserType(effectiveRole); // Keep userType in sync for backward compatibility
        setCollegeId(storedCollegeId);
        
        // Verify token by fetching current user or company with timeout
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout')), 10000)
        );
        
        try {
          if (effectiveRole === 'company') {
            const response = await Promise.race([
              getCurrentCompany(),
              timeoutPromise
            ]);
            setCompany(response.company);
          } else {
            const response = await Promise.race([
              getCurrentUser(),
              timeoutPromise
            ]);
            setUser(response.user);
          }
        } catch (apiError) {
          // If API call fails, clear auth data but don't redirect yet
          // Let the route protection handle the redirect
          console.error('Auth verification failed:', apiError);
          localStorage.removeItem('token');
          localStorage.removeItem('mode');
          localStorage.removeItem('role');
          localStorage.removeItem('collegeId');
          localStorage.removeItem('userType');
          setToken(null);
          setUser(null);
          setCompany(null);
          setMode(null);
          setRole(null);
          setCollegeId(null);
          setUserType(null);
        }
      }
    } catch (err) {
      // Unexpected error - clear auth data
      console.error('Auth check failed:', err);
      localStorage.removeItem('token');
      localStorage.removeItem('mode');
      localStorage.removeItem('role');
      localStorage.removeItem('collegeId');
      localStorage.removeItem('userType');
      setToken(null);
      setUser(null);
      setCompany(null);
      setMode(null);
      setRole(null);
      setCollegeId(null);
      setUserType(null);
    } finally {
      setInitializing(false);
    }
  };

  const storeStudentSession = (response, selectedMode) => {
    localStorage.setItem('token', response.token);
    const userMode = selectedMode || response.user.mode || 'open';
    localStorage.setItem('mode', userMode);
    localStorage.setItem('role', 'student');
    localStorage.setItem('userType', 'student'); // Legacy support

    const userCollegeId = response.user.collegeName || null;
    if (userCollegeId) {
      localStorage.setItem('collegeId', userCollegeId);
    } else {
      localStorage.removeItem('collegeId');
    }

    setToken(response.token);
    setUser(response.user);
    setMode(userMode);
    setRole('student');
    setUserType('student'); // Legacy support
    setCollegeId(userCollegeId);

    return { success: true, mode: userMode };
  };

  const signup = async (userData, selectedMode) => {
    try {
      setLoading(true);
      setError(null);

      // Add mode to user data
      const signupData = {
        ...userData,
        mode: selectedMode
      };

      const response = await apiSignup(signupData);

      if (response.requiresEmailVerification) {
        console.log('[AuthContext] Signup requires email verification', {
          email: response.email,
          mode: response.mode || selectedMode
        });
        return {
          success: true,
          requiresEmailVerification: true,
          email: response.email,
          mode: response.mode || selectedMode,
          message: response.message
        };
      }

      return storeStudentSession(response, selectedMode);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Signup failed. Please try again.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiLogin({
        email: email.trim().toLowerCase(),
        password,
      });

      return storeStudentSession(response);
    } catch (err) {
      const status = err.response?.status;
      let errorMessage = err.response?.data?.message || 'Login failed. Please check your credentials.';
      if (status === 401) {
        errorMessage =
          'Invalid email or password. If you have not signed up yet, create an account first. Company accounts must use the Company tab.';
      }
      if (err.response?.data?.requiresEmailVerification) {
        const verificationResponse = err.response.data;
        console.log('[AuthContext] Login blocked until email verification', {
          email: verificationResponse.email,
          mode: verificationResponse.mode
        });
        return {
          success: false,
          requiresEmailVerification: true,
          email: verificationResponse.email,
          mode: verificationResponse.mode,
          message: verificationResponse.message || 'Please verify your email before logging in.'
        };
      }

      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const verifyEmailOtp = async ({ email, otp, mode: selectedMode }) => {
    try {
      setLoading(true);
      setError(null);
      console.log('[AuthContext] Verifying email OTP', { email, mode: selectedMode });
      const response = await apiVerifyEmailOtp({ email, otp, mode: selectedMode });
      return storeStudentSession(response, selectedMode || response.mode);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'OTP verification failed. Please try again.';
      console.error('[AuthContext] Email OTP verification failed:', errorMessage);
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const resendEmailOtp = async ({ email }) => {
    try {
      console.log('[AuthContext] Resending email OTP', { email });
      const response = await apiResendEmailOtp({ email });
      return { success: true, ...response };
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Could not resend OTP. Please try again.';
      console.error('[AuthContext] Resend email OTP failed:', errorMessage);
      return {
        success: false,
        message: errorMessage,
        retryAfter: err.response?.data?.retryAfter
      };
    }
  };

  const forgotPassword = async (email) => {
    try {
      console.log('[AuthContext] Requesting password reset OTP', { email });
      const response = await apiForgotPassword({ email: email.trim().toLowerCase() });
      return { success: true, ...response };
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Could not send password reset OTP.';
      console.error('[AuthContext] Forgot password failed:', errorMessage);
      return { success: false, message: errorMessage, retryAfter: err.response?.data?.retryAfter };
    }
  };

  const verifyPasswordResetOtp = async ({ email, otp }) => {
    try {
      console.log('[AuthContext] Verifying password reset OTP', { email });
      const response = await apiVerifyPasswordResetOtp({ email, otp });
      return { success: true, ...response };
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Password reset OTP verification failed.';
      console.error('[AuthContext] Password reset OTP verification failed:', errorMessage);
      return { success: false, message: errorMessage };
    }
  };

  const resetPassword = async ({ email, password }) => {
    try {
      console.log('[AuthContext] Resetting password', { email });
      const response = await apiResetPassword({ email, password });
      return { success: true, ...response };
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Password reset failed.';
      console.error('[AuthContext] Password reset failed:', errorMessage);
      return { success: false, message: errorMessage };
    }
  };

  const companySignup = async (companyData) => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiCompanySignup(companyData);
      
      // Store token, mode as 'company', role, and collegeId (null for companies initially)
      localStorage.setItem('token', response.token);
      localStorage.setItem('mode', 'company');
      localStorage.setItem('role', 'company');
      localStorage.setItem('userType', 'company'); // Legacy support
      // Don't set collegeId - companies select it later
      localStorage.removeItem('collegeId');
      
      setToken(response.token);
      setCompany(response.company);
      setMode('company');
      setRole('company');
      setUserType('company'); // Legacy support
      setCollegeId(null);
      
      return { success: true, mode: 'company' };
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Company signup failed. Please try again.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const companyLogin = async (email, password) => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiCompanyLogin({
        email: email.trim().toLowerCase(),
        password,
      });
      
      // Store token, mode as 'company', role, and collegeId (null for companies initially)
      localStorage.setItem('token', response.token);
      localStorage.setItem('mode', 'company');
      localStorage.setItem('role', 'company');
      localStorage.setItem('userType', 'company'); // Legacy support
      // Don't set collegeId - companies select it later
      localStorage.removeItem('collegeId');
      
      setToken(response.token);
      setCompany(response.company);
      setMode('company');
      setRole('company');
      setUserType('company'); // Legacy support
      setCollegeId(null);
      
      return { success: true, mode: 'company' };
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Company login failed. Please check your credentials.';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('mode');
    localStorage.removeItem('role');
    localStorage.removeItem('collegeId');
    localStorage.removeItem('userType');
    setToken(null);
    setUser(null);
    setCompany(null);
    setMode(null);
    setRole(null);
    setCollegeId(null);
    setUserType(null);
    setError(null);
  };

  const refreshCompany = async () => {
    try {
      if (userType === 'company') {
        const response = await getCurrentCompany();
        setCompany(response.company);
      }
    } catch (err) {
      console.error('Error refreshing company data:', err);
    }
  };

  const value = {
    user,
    company,
    setUser,
    setCompany,
    token,
    mode,
    role,
    collegeId,
    setCollegeId: (newCollegeId) => {
      setCollegeId(newCollegeId);
      if (newCollegeId) {
        localStorage.setItem('collegeId', newCollegeId);
      } else {
        localStorage.removeItem('collegeId');
      }
    },
    userType, // Legacy support
    initializing,
    loading,
    error,
    signup,
    login,
    verifyEmailOtp,
    resendEmailOtp,
    forgotPassword,
    verifyPasswordResetOtp,
    resetPassword,
    companySignup,
    companyLogin,
    logout,
    checkAuth,
    refreshCompany
  };

  // Show loading indicator while checking authentication
  if (initializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-page dark:bg-[#0b0b15]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-neutral-400">Loading...</p>
        </div>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
